import { cn } from "@/lib/utils";

interface HeroImageProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  overlay?: boolean;
  overlayClassName?: string;
  objectPosition?: string;
  width?: number;
  height?: number;
  /**
   * When provided, HeroImage emits a width-based srcSet using files at
   * `${base}-${w}.${ext}` for each width plus the original at its natural width.
   * Callers must ensure the variant files exist on disk. Omit for pages that
   * only ship the single full-size source.
   */
  responsiveWidths?: number[];
  /** Natural width of the base (non-suffixed) source; used as the largest srcSet descriptor. */
  baseWidth?: number;
  /** `sizes` attribute for the responsive srcSet. Defaults to `100vw`. */
  sizes?: string;
}

export const HeroImage = ({
  src,
  alt,
  className,
  priority = false,
  overlay = true,
  overlayClassName = "bg-gradient-to-r from-primary/75 via-primary/65 to-primary/55",
  objectPosition = "center",
  width = 1920,
  height = 1080,
  responsiveWidths,
  baseWidth = 1920,
  sizes = "100vw",
}: HeroImageProps) => {
  // Lowercase `fetchpriority` is passed as a raw HTML attribute so React 18 doesn't
  // warn about the camelCase prop and the hint actually reaches the rendered <img>.
  const priorityAttrs = priority
    ? ({ fetchpriority: "high", loading: "eager" } as Record<string, string>)
    : ({ fetchpriority: "auto", loading: "lazy" } as Record<string, string>);

  // Derive AVIF/WebP siblings for JPG/PNG sources (expect matching files on disk).
  const lower = src.toLowerCase();
  const hasRasterExt = lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png");
  const base = hasRasterExt ? src.replace(/\.(jpe?g|png)$/i, "") : null;
  const avifBase = base ? `${base}.avif` : null;
  const webpBase = base ? `${base}.webp` : null;

  // Build width-based srcSet strings for a given extension when responsiveWidths is provided.
  const buildSrcSet = (ext: string): string | undefined => {
    if (!base || !responsiveWidths || responsiveWidths.length === 0) return undefined;
    const parts = responsiveWidths.map((w) => `${base}-${w}.${ext} ${w}w`);
    parts.push(`${base}.${ext} ${baseWidth}w`);
    return parts.join(", ");
  };

  const avifSrcSet = buildSrcSet("avif");
  const webpSrcSet = buildSrcSet("webp");
  const jpgSrcSet = hasRasterExt ? buildSrcSet(lower.endsWith(".png") ? "png" : "jpg") : undefined;

  return (
    <div className="absolute inset-0 z-0 bg-primary">
      <picture>
        {avifBase && (
          <source
            srcSet={avifSrcSet ?? avifBase}
            sizes={avifSrcSet ? sizes : undefined}
            type="image/avif"
          />
        )}
        {webpBase && (
          <source
            srcSet={webpSrcSet ?? webpBase}
            sizes={webpSrcSet ? sizes : undefined}
            type="image/webp"
          />
        )}
        <img
          src={src}
          srcSet={jpgSrcSet}
          sizes={jpgSrcSet ? sizes : undefined}
          alt={alt}
          width={width}
          height={height}
          style={{ objectPosition }}
          className={cn(
            "w-full h-full object-cover will-change-transform",
            className
          )}
          decoding="async"
          {...priorityAttrs}
        />
      </picture>
      {overlay && <div className={cn("absolute inset-0", overlayClassName)} />}
    </div>
  );
};
